import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { TruncatedTooltip } from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useTableSelector } from '../hooks/useTableSelector';
import type { TableSelectorProps } from '../types';

const SELECT_WIDTH = 400;
// The option list is wider than the dropdown so more of each name shows; it still
// stays within the viewport.
const MENU_WIDTH = 560;
// Delay before the full name of a truncated option appears.
const TOOLTIP_ENTER_DELAY_MS = 700;

// The table dropdown with previous/next arrows and the "n/m" position between
// them, compact enough to share a row with the section's title and actions.
export const TableSelector = ({ options, selectedId, onChange }: TableSelectorProps) => {
  const { formatMessage } = useIntl();
  const { selectedIndex, onSelectChange, hasPrevious, hasNext, onStep } = useTableSelector({
    options,
    selectedId,
    onChange,
  });

  if (options.length === 0) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
      <Select
        value={selectedId ?? ''}
        onChange={onSelectChange}
        size="small"
        inputProps={{ 'data-testid': 'analysis-tables-select' }}
        renderValue={(value) => {
          const selected = options.find((option) => option.id === value);
          return (
            <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
              {selected?.label ?? ''}
            </Typography>
          );
        }}
        sx={{
          borderRadius: `${borderRadius.md}px`,
          display: 'flex',
          px: 2,
          alignItems: 'center',
          width: SELECT_WIDTH,
        }}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                width: MENU_WIDTH,
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: `${borderRadius.md}px`,
              },
            },
          },
        }}
      >
        {options.map((option) => {
          const isSelected = option.id === selectedId;
          return (
            <MenuItem
              key={option.id}
              value={option.id}
              data-testid={`analysis-tables-select-option-${option.id}`}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ width: '100%', minWidth: 0 }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  {/* Shown below the pointer and ignoring it, so it covers neither the
                      option being read nor the click that picks it. */}
                  <TruncatedTooltip
                    id={`analysis-tables-select-option-label-${option.id}`}
                    title={option.label}
                    width="auto"
                    placement="bottom-start"
                    followCursor
                    disableInteractive
                    enterDelay={TOOLTIP_ENTER_DELAY_MS}
                    sx={{ mt: 6 }}
                  >
                    <Typography variant="body2" noWrap>
                      {option.label}
                    </Typography>
                  </TruncatedTooltip>

                  <Typography variant="body2" color="text.secondary" noWrap>
                    {option.countLabel}
                  </Typography>
                </Stack>
                {isSelected && (
                  <CheckCircleIcon
                    fontSize="small"
                    color="success"
                    data-testid={`analysis-tables-select-check-${option.id}`}
                  />
                )}
              </Stack>
            </MenuItem>
          );
        })}
      </Select>
      <Stack direction="row" alignItems="center">
        <IconButton
          size="small"
          onClick={() => onStep(-1)}
          disabled={!hasPrevious}
          aria-label={formatMessage({ id: 'analysisDetail.tables.selector.previous' })}
          data-testid="analysis-tables-select-previous"
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="analysis-tables-select-position"
        >
          {selectedIndex + 1}/{options.length}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onStep(1)}
          disabled={!hasNext}
          aria-label={formatMessage({ id: 'analysisDetail.tables.selector.next' })}
          data-testid="analysis-tables-select-next"
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
};
